import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Chrome, UserPlus, Loader2, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

export default function Register({ user, setUser }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return;
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { name, email, password });
      setUser(res.data);
      toast.success(`Bienvenido a BulkBytes, ${res.data.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="register-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4">
            <span className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              BulkBytes
            </span>
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Crear Cuenta
          </CardTitle>
          <CardDescription>
            Regístrate para acceder a ofertas grupales exclusivas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            className="w-full h-12 text-base"
            variant="outline"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
          >
            <Chrome className="mr-2 h-5 w-5" />
            Registrarse con Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">o con email</span>
            </div>
          </div>

          <form onSubmit={handleRegister} className="space-y-3">
            <div>
              <Label htmlFor="reg-name">Nombre</Label>
              <Input
                id="reg-name"
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-email">Email</Label>
              <Input
                id="reg-email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="reg-password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="reg-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-orange-500 hover:bg-orange-600"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Crear Cuenta
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium" data-testid="go-to-login-link">
              Inicia sesión
            </Link>
          </p>
          <p className="text-center text-xs text-muted-foreground">
            Al registrarte, aceptas nuestros{" "}
            <Link to="/terms" className="underline">términos</Link> y{" "}
            <Link to="/privacy" className="underline">política de privacidad</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
